import { useState, useEffect } from 'react';
import { BudgetCard } from '../components/BudgetCard';
import { apiClient } from '../lib/apiClient';
import { formatCurrency, formatNumber } from '../lib/formatters';

interface Campaign {
  campaign_id: string;
  campaign_name: string;
  platform: string;
  spend: number;
  budget: number;
  utilization_pct: number;
  status: 'ok' | 'warning' | 'exceeded';
  remaining: number;
  daily_spend: number;
  daily_budget: number;
  projected_end?: string;
}

interface Summary {
  total_spend: number;
  total_budget: number;
  total_utilization: number;
  campaigns_ok: number;
  campaigns_warning: number;
  campaigns_exceeded: number;
}

export default function SpendDashboard() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [period, setPeriod] = useState<'daily' | 'monthly'>('monthly');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get('/analytics/spend', {
        params: { period }
      });
      
      setCampaigns(response.data.campaigns || []);
      setSummary(response.data.summary || null);
    } catch (err) {
      // Use placeholder data when API is unavailable
      console.warn('API unavailable, using placeholder data');
      setCampaigns([
        {
          campaign_id: 'camp_001',
          campaign_name: 'Summer Sale 2026',
          platform: 'meta',
          spend: 12450.75,
          budget: 15000,
          utilization_pct: 83,
          status: 'warning',
          remaining: 2549.25,
          daily_spend: 415.02,
          daily_budget: 500
        },
        {
          campaign_id: 'camp_002',
          campaign_name: 'Brand Awareness Q1',
          platform: 'google',
          spend: 4320.50,
          budget: 10000,
          utilization_pct: 43,
          status: 'ok',
          remaining: 5679.50,
          daily_spend: 144.02,
          daily_budget: 333.33
        },
        {
          campaign_id: 'camp_003',
          campaign_name: 'Product Launch',
          platform: 'tiktok',
          spend: 8120.00,
          budget: 8000,
          utilization_pct: 102,
          status: 'exceeded',
          remaining: -120.00,
          daily_spend: 270.67,
          daily_budget: 266.67
        }
      ]);
      setSummary({
        total_spend: 24891.25,
        total_budget: 33000,
        total_utilization: 75.4,
        campaigns_ok: 1,
        campaigns_warning: 1,
        campaigns_exceeded: 1
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading spend tracking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md p-6 text-center bg-white rounded-lg shadow-lg dark:bg-gray-800">
          <div className="mb-4 text-red-500">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">Error Loading Data</h3>
          <p className="mb-4 text-gray-600 dark:text-gray-400">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 text-white bg-blue-500 rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <header className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Spend Tracking</h1>
            <p className="mt-1 text-gray-500 dark:text-gray-400">Monitor campaign budgets and prevent overspend</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPeriod('daily')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                period === 'daily'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700'
              }`}
            >
              Daily
            </button>
            <button
              onClick={() => setPeriod('monthly')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                period === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700'
              }`}
            >
              Monthly
            </button>
          </div>
        </header>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-white rounded-lg shadow dark:bg-gray-800">
              <div className="text-sm text-gray-500 dark:text-gray-400">Total Spend</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(summary.total_spend)}</div>
              <div className="text-xs text-gray-500">of {formatCurrency(summary.total_budget)} budget</div>
            </div>
            
            <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-lg shadow dark:bg-green-900/20">
              <div className="flex items-center gap-2">
                <span className="text-2xl">✅</span>
                <div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">On Track</div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-400">{summary.campaigns_ok}</div>
                </div>
              </div>
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">Campaigns under 80%</div>
            </div>

            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg shadow dark:bg-yellow-900/20">
              <div className="flex items-center gap-2">
                <span className="text-2xl">⚠️</span>
                <div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">Warning</div>
                  <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">{summary.campaigns_warning}</div>
                </div>
              </div>
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">Campaigns 80-100%</div>
            </div>

            <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow dark:bg-red-900/20">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🚨</span>
                <div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">Exceeded</div>
                  <div className="text-2xl font-bold text-red-700 dark:text-red-400">{summary.campaigns_exceeded}</div>
                </div>
              </div>
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">Over budget</div>
            </div>
          </div>
        )}

        {/* Campaign Budget Cards */}
        <div className="space-y-4">
          {campaigns.length === 0 ? (
            <div className="py-12 text-center bg-white rounded-lg shadow dark:bg-gray-800">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
                No campaigns with budgets
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Set budgets for your campaigns to track spending
              </p>
            </div>
          ) : (
            campaigns.map((campaign) => (
              <BudgetCard key={campaign.campaign_id} campaign={campaign} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
